import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { disabled, form, FormField } from '@angular/forms/signals';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { ActivatedRoute } from '@angular/router';
import { DualLoginUiService } from './dual-login.ui-service';
import type { LoginIssuer } from '../login-issuer.store';
import { LoginComponent } from './login/login.component';

/**
 * Identifier-first login screen: the user enters their email or phone
 * number (#29713). Emails run realm discovery (Entra for corporate
 * domains, Auth0 otherwise); phone numbers always route to Auth0 —
 * Entra accounts are UPN/email-based. The matching redirect flow starts
 * with the identifier as the login hint. A "use UPS1 account instead"
 * escape covers users whose email looks corporate but who have no AD
 * account (field hands). Host apps route their unauthenticated landing
 * here and register `buildEntraMsalProviders(...)`; with no Entra
 * registration every identifier routes to Auth0 unchanged.
 */
@Component({
  selector: 'ups-dual-login',
  standalone: true,
  imports: [CommonModule, FormField, ButtonModule, LoginComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DualLoginUiService],
  template: `
    <div class="login-page">
      @if (internalAuthLogin()) {
        <ups-internal-login
          [identifier]="internalPrefill()"
          (useDifferentProvider)="internalAuthLogin.set(false)"
        />
      } @else if (returningIssuer(); as issuer) {
        <div class="login-card">
          <h1 class="login-title">Welcome back</h1>
          <p class="login-subtitle">
            Continue with your
            {{ issuer === 'entra' ? 'Microsoft' : 'UPS1' }} account
          </p>

          <button
            kendoButton
            type="button"
            themeColor="primary"
            class="login-button"
            [disabled]="isRedirecting()"
            (click)="continueAsReturningUser(issuer)"
          >
            Continue
          </button>

          <button
            kendoButton
            type="button"
            fillMode="link"
            class="login-link"
            [disabled]="isRedirecting()"
            (click)="useDifferentAccount()"
          >
            Use a different account
          </button>

          @if (errorMessage()) {
            <p class="login-error" role="alert">{{ errorMessage() }}</p>
          }
        </div>
      } @else {
        <form class="login-card" (submit)="onIdentifierSubmit($event)">
          <h1 class="login-title">Sign in</h1>
          <p class="login-subtitle">
            Enter your email address or phone number to continue
          </p>

          <input
            class="k-input k-input-md login-input"
            type="text"
            autocomplete="username"
            placeholder="you@universalplant.com or 555-123-4567"
            [formField]="identifierForm"
          />

          <button
            kendoButton
            type="submit"
            themeColor="primary"
            class="login-button"
            [disabled]="isRedirecting()"
          >
            Continue
          </button>

          @if (ups1Available) {
            <button
              kendoButton
              type="button"
              fillMode="link"
              class="login-link w-100"
              [disabled]="isRedirecting()"
              (click)="signInWithUps1()"
            >
              Sign in with a UPS1 account instead
            </button>
          }

          @if (dualLogin.internalAvailable()) {
            <button
              kendoButton
              type="button"
              fillMode="link"
              class="login-link w-100"
              [disabled]="isRedirecting()"
              (click)="internalAuthLogin.set(true)"
            >
              Sign in with an internal account
            </button>
          }
          @if (errorMessage()) {
            <p class="login-error" role="alert">{{ errorMessage() }}</p>
          }
        </form>
      }
    </div>
  `,
  styles: [
    `
      /* Flat selectors: an optimized build turns nesting under :host::ng-deep into :is(), which matches nothing. */
      :host ::ng-deep .login-page {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: var(--color-background, #f5f5f5);
      }
      :host ::ng-deep .login-card {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        width: 22rem;
        padding: 2rem;
        border-radius: 0.5rem;
        background: var(--color-surface, #fff);
        box-shadow: 0 2px 12px rgb(0 0 0 / 0.12);
      }
      :host ::ng-deep .login-title {
        margin: 0;
        font-size: 1.375rem;
      }
      :host ::ng-deep .login-subtitle {
        margin: 0 0 0.5rem;
        color: var(--color-text-secondary, #666);
      }
      :host ::ng-deep .login-input,
      :host ::ng-deep .login-button {
        width: 100%;
      }
      :host ::ng-deep .login-link {
        align-self: center;
      }
      :host ::ng-deep .login-error {
        margin: 0.25rem 0 0;
        color: var(--color-error, #b00020);
        font-size: 0.875rem;
      }
      :host ::ng-deep .login-success {
        margin: 0.25rem 0 0;
        color: #4fb000;
        font-size: 0.875rem;
      }
    `,
  ],
})
export class DualLoginComponent {
  protected readonly dualLogin = inject(DualLoginUiService);
  private readonly route = inject(ActivatedRoute, { optional: true });

  readonly identifier = signal('');
  /** WI 36701: the typed identifier fills the internal card's E-mail box, but only when it is an email. */
  readonly internalPrefill = computed(() => {
    const value = this.identifier().trim();
    return this.dualLogin.classifyIdentifier(value) === 'email' ? value : '';
  });
  readonly isRedirecting = signal(false);
  readonly errorMessage = signal('');
  /** Config-time constant: no UPS1 escape in an app with no Auth0 client (#36633). */
  readonly ups1Available = this.dualLogin.ups1Available();
  /**
   * Disabled while a redirect starts; in the schema, not the template (checklist 11, NG8022).
   * `name` keeps the input's name "identifier" (password managers match saved logins on it);
   * without it Angular names it `${APP_ID}.form<N>`, which varies per session.
   */
  protected readonly identifierForm = form(
    this.identifier,
    (p) => {
      disabled(p, () => this.isRedirecting());
    },
    { name: 'identifier' }
  );

  /**
   * Returning users skip the email step (#29709): one click continues with
   * the remembered issuer. Deliberately NOT an auto-redirect — logout lands
   * here, and an unconditional redirect would make signing out impossible.
   */
  readonly returningIssuer = signal(this.dualLogin.returningIssuer());

  readonly internalAuthLogin = linkedSignal(
    () =>
      this.dualLogin.internalAvailable() &&
      this.returningIssuer() === 'internal'
  );

  constructor() {
    // A session that is already authenticated does not belong on the login
    // page — MSAL redirect logins land back HERE, not on the redirectUri
    // (#29713). Logged-out visitors report unauthenticated and stay.
    this.dualLogin.forwardAuthenticatedUser(this.returnTarget());
  }

  /** 'internal' reaches here only when internal sign-in is unavailable, and continues as UPS1 like 'auth0'. */
  continueAsReturningUser(issuer: LoginIssuer): void {
    this.errorMessage.set('');
    this.isRedirecting.set(true);
    try {
      if (issuer === 'entra') {
        this.dualLogin.loginWithEntra();
      } else {
        this.dualLogin.loginWithUps1(this.returnTarget());
      }
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }

  /** "Use a different account": forget the issuer, show the email form. */
  useDifferentAccount(): void {
    this.dualLogin.forgetIssuer();
    this.errorMessage.set('');
    this.returningIssuer.set(null);
  }

  private returnTarget(): string {
    return this.route?.snapshot.queryParamMap.get('returnTo') ?? '/';
  }

  /**
   * The identifier form has no NgForm to swallow the native submit any more (conversion checklist 12), so Enter or
   * Continue would reload the login page: prevent it here.
   */
  onIdentifierSubmit(event: Event): void {
    event.preventDefault();
    this.continueWithIdentifier();
  }

  continueWithIdentifier(): void {
    const identifier = this.identifier().trim();
    const kind = this.dualLogin.classifyIdentifier(identifier);
    if (!kind) {
      this.errorMessage.set('Please enter your email address or phone number.');
      return;
    }
    this.errorMessage.set('');
    const toEntra =
      kind === 'email' && this.dualLogin.discoverIssuer(identifier) === 'entra';
    if (!toEntra && this.dualLogin.internalInsteadOfUps1()) {
      // No Auth0 client here: new hires sign in with InternalAuth (#36633).
      this.internalAuthLogin.set(true);
      return;
    }
    this.isRedirecting.set(true);
    try {
      if (kind === 'phone') {
        // Phones are always Auth0 — Entra accounts are UPN/email-based.
        this.dualLogin.loginWithUps1(
          this.returnTarget(),
          this.dualLogin.normalizePhone(identifier)
        );
      } else if (this.dualLogin.discoverIssuer(identifier) === 'entra') {
        this.dualLogin.loginWithEntra(identifier);
      } else {
        this.dualLogin.loginWithUps1(this.returnTarget(), identifier);
      }
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }

  /** Field-hand escape: corporate-looking email but no AD account. */
  signInWithUps1(): void {
    this.errorMessage.set('');
    this.isRedirecting.set(true);
    try {
      this.dualLogin.loginWithUps1(
        this.returnTarget(),
        this.identifier().trim() || undefined
      );
    } catch (err) {
      this.isRedirecting.set(false);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Sign-in failed to start.'
      );
    }
  }
}
